import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import router from '@Site/Router'
import getBrowserLanguages from '@Utils/GetBrowserLanguages'
import SocialAuthButton from './SocialAuthButton'
import AuthInput from './AuthInput'
import submitGoogleOauthForm from './SubmitGoogleOauthForm'
import Button from '@SiteComponents/Button'
import GoogleIcon from '@Images/google-icon.svg'

import {
  EMAIL_MAX_LENGTH,
} from '@Utils/EmailSanitizerAndValidator'

import {
  validateEmail,
  validatePassword,
  PASSWORD_MAX_LENGTH,
  handleBlurField,
  handleChangeField
} from '@Utils/AuthenticationValidationHelpers'

import {
  signUp,
} from '@SiteModules/Authentication/actions'

import {
  getLoading,
} from '@SiteModules/Authentication/selectors'

import {
  FormErrors,
} from '@SiteModules/Authentication/types'

const MARGIN_TO_SHOW_CHAR_COUNT = 20

const SignUpForm: React.FC = () => {
  const dispatch = useDispatch()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const loading = useSelector(getLoading)
  const hasErrors = Object.values(errors).some(Boolean)
  const isDisabled = !email.trim() || !password.trim() || !termsAccepted || loading || hasErrors


  const handleEmailBlur = () => {
    handleBlurField('email', email, setTouched, setErrors, validateEmail)
  }

  const handlePasswordBlur = () => {
    handleBlurField('password', password, setTouched, setErrors, validatePassword)
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const languages = getBrowserLanguages()

    dispatch(signUp({ email, password, passwordConfirmation: password, languages }))
  }

  const goToSignIn = () => {
    router.navigate('/users/sign_in')
  }

  const forwardToGoogle = () => {
    submitGoogleOauthForm()
  }

  const openTermsAndConditions = () => {
    window.open('https://links.checksub.com/CGV')
  }

  const openPrivacyPolicy = () => {
    window.open('https://www.iubenda.com/privacy-policy/59734139?_gl=1*9x2l1p*_up*MQ..*_ga*MTI3Mzg5MDg5MC4xNzczMjA0NjA1*_ga_C1BQTG9KPW*czE3NzMyMDQ2MDQkbzEkZzAkdDE3NzMyMDQ2MDQkajYwJGwwJGgxNzU0MzA1MzE2')
  }

  const handleEmailChange = (val: string) => {
    setEmail(val)
    handleChangeField('email', val, touched, setErrors, validateEmail)
  }

  const handlePasswordChange = (val: string) => {
    setPassword(val)
    handleChangeField('password', val, touched, setErrors, validatePassword)
  }

  const handleAcceptTerms = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTermsAccepted(e.target.checked)
  }

  const renderTitle = () => {
    return (
      <React.Fragment>
        <h1 className="FormPanel__title">Turn your videos into growth engines.</h1>
        <p className="FormPanel__subtitle">Join 10,000+ marketers and creators.</p>
      </React.Fragment>
    )
  }

  const renderOtherAuth = () => {
    return (
      <div className="AuthLayout__social-auth-button-row">
        <SocialAuthButton
          label="Continue with Google"
          onClick={forwardToGoogle}
          icon={<GoogleIcon />}
          data-testid="signup-with-google-submit"
        />
      </div>
    )
  }

  const renderDivider = () => {
    return (
      <div className="AuthLayout__divider">
        <div className="AuthLayout__divider-line" />
        <span className="AuthLayout__divider-text">or with email</span>
        <div className="AuthLayout__divider-line" />
      </div>
    )
  }

  const renderInputs = () => {
    return (
      <React.Fragment>
        <AuthInput
          id="signup-email"
          label="Work Email"
          type="email"
          placeholder="you@company.com"
          value={email}
          autoComplete="username"
          data-testid="signup-email-input"
          onChange={handleEmailChange}
          onBlur={handleEmailBlur}
          error={errors.email}
          maxLength={EMAIL_MAX_LENGTH}
          showCharCount={email.length > EMAIL_MAX_LENGTH - MARGIN_TO_SHOW_CHAR_COUNT}
        />
        <AuthInput
          id="signup-password"
          label="Password"
          placeholder="Create a password"
          value={password}
          autoComplete="new-password"
          data-testid="signup-password-input"
          onChange={handlePasswordChange}
          onBlur={handlePasswordBlur}
          error={errors.password}
          maxLength={PASSWORD_MAX_LENGTH}
          showCharCount={password.length > PASSWORD_MAX_LENGTH - MARGIN_TO_SHOW_CHAR_COUNT}
          isPassword
        />
      </React.Fragment>
    )
  }

  const renderTerms = () => {
    return (
      <div className="AuthLayout__checkbox-wrapper">
        <label className="AuthLayout__checkbox-label" data-testid="signup-terms-accept">
          <input
            type="checkbox"
            className="AuthLayout__checkbox"
            data-testid="signup-terms-checkbox"
            checked={termsAccepted}
            onChange={handleAcceptTerms}
          />
          <div className="AuthLayout__checkbox-checkmark-wrapper" data-testid="signup-terms-toggle">
            <div className="AuthLayout__checkbox-checkmark"/>
          </div>
          <span className="AuthLayout__checkbox-text">
            I agree to the{' '}
            <button
              type="button"
              className="FormPanel__footer-link FormPanel__footer-link-no-margin"
              onClick={openTermsAndConditions}
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              className="FormPanel__footer-link FormPanel__footer-link-no-margin"
              onClick={openPrivacyPolicy}
            >
              Privacy Policy
            </button>
          </span>
        </label>
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <React.Fragment>
        <div className="FormPanel__button-area">
          <Button
            styleType="brand"
            size="large"
            caption="Create Account"
            type="submit"
            disabled={isDisabled}
            data-testid="signup-with-email-submit"
          />
        </div>
        <div className="FormPanel__footer">
          <span className="FormPanel__footer-text">Already have an account?</span>
          <button type="button" className="FormPanel__footer-link" onClick={goToSignIn}>
            Log in
          </button>
        </div>
      </React.Fragment>
    )
  }

  return (
    <form className="SignUpForm" onSubmit={submit} noValidate>
      {renderTitle()}
      {renderOtherAuth()}
      {renderDivider()}
      {renderInputs()}
      {renderTerms()}
      {renderBottom()}
    </form>
  )
}

export default SignUpForm
